import { Link, NavLink } from "react-router-dom";
import { useAuth } from "@/lib/AuthProvider";
import { Menu, X, Phone } from "lucide-react";
import { useState } from "react";
import CurrencySwitcher from "@/components/CurrencySwitcher";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/consult", label: "Consult" },
  { to: "/shop", label: "Shop" },
  { to: "/about", label: "About" },
  { to: "/book", label: "Book" },
  { to: "/contact", label: "Contact" },
];

const PHONE_DIGITS = "2348139771270";

export default function Header() {
  const { user, login, logout } = useAuth();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 bg-[#FFF7E6]/90 backdrop-blur-xl border-b border-[#EAD9B0]" data-testid="site-header">
      <div className="stripe-band h-1" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20 md:h-28">
        <Link to="/" className="flex items-center gap-4" data-testid="brand-link">
          <img src="/logo.png" alt="Jay's Veterinary Consult and Services"
               className="h-20 md:h-24 lg:h-28 w-auto floaty" />
          <div className="hidden md:block leading-tight">
            <div className="font-display text-xl lg:text-2xl font-semibold text-[#1C1917]">Jay's Veterinary</div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#0F766E] font-semibold">Consult &amp; Services</div>
            <div className="text-[10px] text-[#57534E] mt-0.5">with Dr. Juwon · DVM</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${isActive ? "text-[#0F766E]" : "text-[#1C1917] hover:text-[#0F766E]"}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <CurrencySwitcher compact />
          <a href={`tel:+${PHONE_DIGITS}`} className="text-sm text-[#1C1917] hover:text-[#0F766E] flex items-center gap-1" data-testid="header-phone">
            <Phone size={14} /> +234 813 977 1270
          </a>
          {user ? (
            <>
              <Link to={user.role !== "client" ? "/admin" : "/portal"}
                className="px-4 py-2 rounded-full text-sm font-medium border border-[#0F766E] text-[#0F766E] hover:bg-[#0F766E] hover:text-white transition-colors"
                data-testid="header-portal-btn">
                {user.role !== "client" ? "Dashboard" : "Portal"}
              </Link>
              <button onClick={logout} className="text-sm text-[#57534E] hover:text-[#1C1917]" data-testid="header-logout-btn">Sign out</button>
            </>
          ) : (
            <>
              <button onClick={login} className="text-sm font-medium text-[#0F766E] hover:text-[#0B5A55]" data-testid="header-signin-btn">Sign in</button>
              <Link to="/book" className="btn-primary px-5 py-2.5 rounded-full text-sm font-semibold" data-testid="header-book-btn">Book appointment</Link>
            </>
          )}
        </div>

        <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Toggle menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#EAD9B0] bg-[#FFF7E6]" data-testid="mobile-nav">
          <div className="px-4 py-4 flex flex-col gap-3">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="py-2 text-[#1C1917] font-medium" data-testid={`mobile-nav-${n.label.toLowerCase()}`}>{n.label}</Link>
            ))}
            <a href={`tel:+${PHONE_DIGITS}`} className="py-2 text-[#0F766E] font-medium flex items-center gap-2"><Phone size={14} /> +234 813 977 1270</a>
            <div className="py-2"><CurrencySwitcher /></div>
            <div className="border-t border-[#EAD9B0] pt-3 flex flex-col gap-2">
              {user ? (
                <>
                  <Link to={user.role !== "client" ? "/admin" : "/portal"} onClick={() => setOpen(false)} className="btn-primary text-center py-2.5 rounded-full text-sm font-semibold">
                    {user.role !== "client" ? "Dashboard" : "Portal"}
                  </Link>
                  <button onClick={logout} className="py-2 text-[#57534E] text-sm">Sign out</button>
                </>
              ) : (
                <>
                  <button onClick={login} className="py-2 text-left text-[#0F766E] text-sm font-medium">Sign in</button>
                  <Link to="/book" onClick={() => setOpen(false)} className="btn-primary text-center py-2.5 rounded-full text-sm font-semibold">Book appointment</Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
